/**
 * Placement detail page with analytics and tabbed interface.
 */

import { useParams } from 'react-router-dom';
import { usePlacement } from '@/hooks/usePlacements';
import { Card } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/ui/loading';
import { ArrowLeft, Copy, ExternalLink, BarChart3, Network, Link as LinkIcon, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import { PlacementOverview } from './tabs/overview';
import { PlacementCascade } from './tabs/cascade';
import { PlacementLinks } from './tabs/links';
import { PlacementContext } from './tabs/context';
import './detail.scss';

export function PlacementDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: placementData, isLoading } = usePlacement(id || '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!placementData) {
    return (
      <div className="text-center py-12">
        <p className="of-text-muted">Placement not found</p>
        <Link to="/placements" style={{ marginTop: 'var(--of-space-4)', display: 'inline-block' }}>
          <Button variant="primary">Back to Placements</Button>
        </Link>
      </div>
    );
  }

  const { placement, kpis } = placementData;
  // TODO: Use domain.domain_name from placement relationship instead of hardcoding
  // For now, using default domain - should be fetched from domain_id
  const trackingLink = `https://${placement.placement_id}.origynflow.com`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(trackingLink);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <BarChart3 /> },
    { id: 'cascade', label: 'Cascade', icon: <Network /> },
    { id: 'links', label: 'Links', icon: <LinkIcon /> },
    { id: 'context', label: 'Context', icon: <FileText /> },
  ];

  return (
    <div className="placement-detail">
      <div className="placement-detail__header">
        <div className="placement-detail__header-back">
          <Link to="/placements">
            <ArrowLeft />
          </Link>
          <div>
            <h1 className="h1">Placement Details</h1>
            <p className="subtitle">{placement.destination_url}</p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="placement-detail__kpi-grid">
        <Card>
          <div className="placement-detail__kpi-card">
            <p>Clicks (24h)</p>
            <p>{kpis.clicks_24h}</p>
          </div>
        </Card>
        <Card>
          <div className="placement-detail__kpi-card">
            <p>Uniques (24h)</p>
            <p>{kpis.uniques_24h}</p>
          </div>
        </Card>
        <Card>
          <div className="placement-detail__kpi-card">
            <p>Bots (24h)</p>
            <p>{kpis.bots_24h}</p>
          </div>
        </Card>
      </div>

      {/* Tracking Link */}
      <Card>
        <h2 className="h3" style={{ marginBottom: 'var(--of-space-4)' }}>Tracking Link</h2>
        <div className="placement-detail__tracking-link">
          <code className="placement-detail__tracking-link-code">
            {trackingLink}
          </code>
          <div className="placement-detail__tracking-link-actions">
            <Button
              onClick={handleCopyLink}
              variant="secondary"
              title="Copy link"
            >
              <Copy style={{ width: '1rem', height: '1rem' }} />
            </Button>
            <Button
              as="a"
              href={trackingLink}
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
            >
              <ExternalLink style={{ width: '1rem', height: '1rem' }} />
            </Button>
          </div>
        </div>
      </Card>

      {/* Tabbed Content */}
      <Card>
        <Tabs tabs={tabs} defaultTab="overview">
          {(activeTab) => {
            switch (activeTab) {
              case 'overview':
                return <PlacementOverview placementId={id || ''} />;
              case 'cascade':
                return <PlacementCascade placementId={id || ''} />;
              case 'links':
                return <PlacementLinks placementId={id || ''} trackingLink={trackingLink} />;
              case 'context':
                return <PlacementContext placement={placement} />;
              default:
                return null;
            }
          }}
        </Tabs>
      </Card>
    </div>
  );
}

